import { createTheme } from '@mui/material';

const palette = {
  primary: {
    light: '#ffffff',
    dark: 'rgb(18, 19, 28)',
    main: '#d62246',
    contrastText: '#ffffff',
  },
  secondary: {
    light: '#4f567d',
    dark: '#2a2d3a',
    main: '#4f567d',
    contrastText: '#ffffff',
  },
  background: {
    // background colors
    default: 'rgba(37, 39, 55, 0.95)',
    secondary: 'rgba(33, 36, 51, 0.8)',
    transparent: 'rgba(18, 19, 28, 0.9)',
    disabled: '#808080',
    paper: 'rgba(18, 19, 28, 0.95)',
    card: 'rgba(37, 39, 55, 0.8)',

    // hover background colors
    hover: {
      dark: '#4f567d',
      light: '#1976d2',
    },

    // active background color
    active: '#d62246',
  },
  text: {
    primary: 'rgb(240, 240, 240)',
    secondary: 'rgb(143, 146, 161)',
    disabled: '#808080',
  },
  border: {
    // border colors
    main: 'rgba(240, 240, 240, 0.2)',
    disabled: '#808080',
    card: 'rgba(240, 240, 240, 0.1)',

    // border colors on hover
    hover: {
      light: '#1976d2',
    },
  },
  paragraph: '16px',
};

export const theme = createTheme({
  palette: { ...palette },
  typography: {
    fontFamily: 'Manrope, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.15)',
    '0px 8px 16px rgba(0,0,0,0.2)',
    '0px 12px 24px rgba(0,0,0,0.25)',
    '0px 16px 32px rgba(0,0,0,0.3)',
    '0px 20px 40px rgba(0,0,0,0.35)',
    '0px 24px 48px rgba(0,0,0,0.4)',
    '0px 28px 56px rgba(0,0,0,0.45)',
    '0px 32px 64px rgba(0,0,0,0.5)',
    '0px 36px 72px rgba(0,0,0,0.55)',
    '0px 40px 80px rgba(0,0,0,0.6)',
    '0px 44px 88px rgba(0,0,0,0.65)',
    '0px 48px 96px rgba(0,0,0,0.7)',
    '0px 52px 104px rgba(0,0,0,0.75)',
    '0px 56px 112px rgba(0,0,0,0.8)',
    '0px 60px 120px rgba(0,0,0,0.85)',
    '0px 64px 128px rgba(0,0,0,0.9)',
    '0px 68px 136px rgba(0,0,0,0.95)',
    '0px 72px 144px rgba(0,0,0,1)',
    '0px 76px 152px rgba(0,0,0,1)',
    '0px 80px 160px rgba(0,0,0,1)',
    '0px 84px 168px rgba(0,0,0,1)',
    '0px 88px 176px rgba(0,0,0,1)',
    '0px 92px 184px rgba(0,0,0,1)',
    '0px 96px 192px rgba(0,0,0,1)',
    '0px 100px 200px rgba(0,0,0,1)',
    '0px 104px 208px rgba(0,0,0,1)',
    '0px 108px 216px rgba(0,0,0,1)',
    '0px 112px 224px rgba(0,0,0,1)',
    '0px 116px 232px rgba(0,0,0,1)',
    '0px 120px 240px rgba(0,0,0,1)',
    '0px 124px 248px rgba(0,0,0,1)',
    '0px 128px 256px rgba(0,0,0,1)',
    '0px 132px 264px rgba(0,0,0,1)',
    '0px 136px 272px rgba(0,0,0,1)',
    '0px 140px 280px rgba(0,0,0,1)',
    '0px 144px 288px rgba(0,0,0,1)',
    '0px 148px 296px rgba(0,0,0,1)',
    '0px 152px 304px rgba(0,0,0,1)',
    '0px 156px 312px rgba(0,0,0,1)',
    '0px 160px 320px rgba(0,0,0,1)',
    '0px 164px 328px rgba(0,0,0,1)',
    '0px 168px 336px rgba(0,0,0,1)',
    '0px 172px 344px rgba(0,0,0,1)',
    '0px 176px 352px rgba(0,0,0,1)',
    '0px 180px 360px rgba(0,0,0,1)',
    '0px 184px 368px rgba(0,0,0,1)',
    '0px 188px 376px rgba(0,0,0,1)',
    '0px 192px 384px rgba(0,0,0,1)',
    '0px 196px 392px rgba(0,0,0,1)',
    '0px 200px 400px rgba(0,0,0,1)',
  ],
  components: {
    MuiTypography: {
      defaultProps: {
        color: 'rgb(240, 240, 240)',
      },
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: palette.background.paper,
          color: palette.text.primary,
          border: `1px solid ${palette.border.card}`,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: palette.background.card,
          border: `1px solid ${palette.border.card}`,
          borderRadius: 16,
          backdropFilter: 'blur(10px)',
          // Remove hover-induced movement
          transition: 'none',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          color: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          margin: '2px 0',
          transition: 'all 0.2s ease',
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
            color: 'white',
          },
          '&[data-focus="true"]': {
            backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
          },
          '&:hover': {
            backgroundColor: 'rgba(239, 68, 68, 0.15) !important',
          },
        },
        paper: {
          background: 'rgba(20, 20, 30, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
        listbox: {
          padding: '8px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          color: 'rgba(255, 255, 255, 0.9)',
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
            color: 'white',
          },
          '&:hover': {
            backgroundColor: 'rgba(239, 68, 68, 0.15) !important',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          background: palette.background.secondary,
          color: palette.text.primary,
          border: `1px solid ${palette.border.card}`,
          borderRadius: 20,
          fontWeight: 500,
          '&:hover': {
            background: palette.background.hover.dark,
          },
        },
        filled: {
          background: palette.background.active,
          color: palette.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 500,
        },
        secondary: {
          color: palette.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          transition: 'all 0.3s ease-in-out',
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            color: palette.text.primary,
            borderColor: palette.border.main,
            background: `${palette.background.default} !important`,
            borderWidth: '2px',
            '&:hover': {
              background: `${palette.background.active} !important`,
              borderColor: `${palette.background.active} !important`,
              color: palette.text.primary,
              boxShadow: '0 4px 12px rgba(214, 34, 70, 0.3)',
            },
            '&.MuiButton-root.Mui-disabled': {
              color: palette.text.disabled,
              borderColor: palette.border.disabled,
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            background: `linear-gradient(135deg, ${palette.background.active}, ${palette.primary.main})`,
            color: palette.text.primary,
            boxShadow: '0 4px 12px rgba(214, 34, 70, 0.3)',
            '&:hover': {
              background: `linear-gradient(135deg, ${palette.primary.main}, ${palette.background.active})`,
              boxShadow: '0 6px 20px rgba(214, 34, 70, 0.4)',
            },
          },
        },
        {
          props: { variant: 'text' },
          style: {
            color: palette.text.primary,
            '&:hover': {
              background: palette.background.hover.dark,
            },
          },
        },
      ],
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: palette.background.secondary,
            border: `1px solid ${palette.border.card}`,
            '&:hover': {
              borderColor: palette.border.main,
            },
            '&.Mui-focused': {
              borderColor: palette.background.active,
              boxShadow: `0 0 0 2px ${palette.background.active}20`,
            },
          },
          '& .MuiInputLabel-root': {
            color: palette.text.secondary,
            '&.Mui-focused': {
              color: palette.background.active,
            },
          },
          '& .MuiInputBase-input': {
            color: palette.text.primary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            background: palette.background.active,
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          minHeight: 48,
          '&.Mui-selected': {
            color: palette.background.active,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: palette.background.paper,
          border: 'none',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: palette.background.paper,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${palette.border.card}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 88,
          '@media (min-width:600px)': {
            minHeight: 112,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width:600px)': {
            paddingLeft: 24,
            paddingRight: 24,
          },
          '@media (min-width:900px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
          '@media (min-width:1200px)': {
            paddingLeft: 40,
            paddingRight: 40,
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            margin: 0,
          },
        },
      },
    },
  },
});
